import type { FC, PropsWithChildren } from 'react';
import { createContext, useCallback, useEffect, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { baseApiClient } from 'api/BaseApiClient';
import { CustomizationQueryKeys } from 'hooks/customization';
import { useUpdateProperties } from 'hooks/properties';
import { useFeatures } from 'shared/lib/features/useFeatures';

import { useAuth } from '../lib/auth/useAuth';
import { getEnv } from '../lib/env/getEnv';

type SandboxContextType = {
  isSandboxOn: boolean;
  isSandboxVisible: boolean;
  tooltipMessage: string;
  onSandboxSwitch: (isSandboxMode: boolean) => boolean;
};

const defaultContextValue: SandboxContextType = {
  isSandboxOn: true,
  isSandboxVisible: true,
  tooltipMessage: '',
  onSandboxSwitch: () => true,
};

export const SandboxContext = createContext(defaultContextValue);

export const SandboxContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [isSandboxOn, setIsSandboxOn] = useState<boolean>(defaultContextValue.isSandboxOn);
  const [tooltipMessage, setTooltipMessage] = useState(defaultContextValue.tooltipMessage);
  const { updateProperties } = useUpdateProperties();

  const envTypes = user?.profile?.env_types;

  const setSandbox = useCallback(
    (isSandbox: boolean) => {
      const env = getEnv(isSandbox, envTypes);

      baseApiClient.setEnv(env);
      setIsSandboxOn(isSandbox);
    },
    [envTypes],
  );

  const role = user?.profile?.role?.id;
  const isOrdersManager = role === 'orders_manager' || role === 'orders_tasks_manager';
  const { data: features } = useFeatures();

  // Both sources have to agree. The keys endpoint is uncached while env_types comes from a cached
  // read, so requiring the key keeps the switch shut until getEnv can actually resolve to prod, and
  // requiring env_types shuts it again as soon as the keys are gone.
  const hasNonSandboxKey = !!(user?.hasProductionKey || user?.hasDevelopmentKey);
  const canUseProduction = hasNonSandboxKey && !!envTypes?.some((envType) => envType !== 'sandbox');
  const prefersSandbox = user?.properties?.isSandboxMode;
  const isAuthenticated = !!user;

  const isSandboxVisible =
    !!user?.profile?.permissions.includes('can_view_sandbox') &&
    !(user?.isCompanyIndividual && !features?.flags?.sandbox_switch_individual_company);

  // Keyed on the values it reads rather than the user object: that object is rebuilt whenever any
  // of the queries behind it settle, and re-running this on an unrelated refetch would overwrite a
  // mode the user just picked.
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    setSandbox(!(canUseProduction && (isOrdersManager || !prefersSandbox)));
  }, [isAuthenticated, canUseProduction, isOrdersManager, prefersSandbox, setSandbox]);

  const onSandboxSwitch = (isSandboxMode: boolean) => {
    setTooltipMessage('');

    if (isOrdersManager) {
      const orderManagerTooltip = canUseProduction
        ? 'You can only use production mode once the Production API keys are issued'
        : 'Production mode will be enabled once the Production API keys are issued';

      setTooltipMessage(orderManagerTooltip);

      return false;
    }

    if (canUseProduction) {
      setSandbox(isSandboxMode);
      updateProperties(
        { isSandboxMode },
        {
          onError(error) {
            // eslint-disable-next-line no-console
            console.error('failed to save sandbox state', error);
          },
          onSuccess() {
            void queryClient.invalidateQueries({
              queryKey: [CustomizationQueryKeys.getWidgetSettings],
            });
          },
        },
      );

      return true;
    }

    return false;
  };

  return (
    <SandboxContext.Provider
      value={{
        isSandboxOn,
        isSandboxVisible,
        tooltipMessage,
        onSandboxSwitch,
      }}
    >
      {children}
    </SandboxContext.Provider>
  );
};
